import { useCallback, useEffect, useState } from "react";
import { Box, Chip, MenuItem, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DisplayCard } from "../whatsapp/datadisplay";
import { CircularLoading } from "../utils/loading";
import { TableWithSearchAndSortEngine } from "../utils/table";
import { openGlobalSnack } from "../utils/snack";
import { formatLocalCurrency } from "../../utils/currency";
import { formatDateWithMin } from "../../utils/date";
import { HS_AGENT, SOURCE_LABEL, centsToReais, hsRequest } from "./api";

export interface CashSummary { available: number; pending: number; total: number; earned: number; paid: number }

export function CashSummaryCards({ summary }: { summary: CashSummary }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', my: 2 }}>
            <DisplayCard primary="Disponível" secondary={formatLocalCurrency(centsToReais(summary.available))} />
            <DisplayCard primary="Pendente (carência)" secondary={formatLocalCurrency(centsToReais(summary.pending))} />
            <DisplayCard primary="Total ganho" secondary={formatLocalCurrency(centsToReais(summary.earned))} />
            <DisplayCard primary="Já recebido" secondary={formatLocalCurrency(centsToReais(summary.paid))} />
        </Box>
    );
}

export function EntriesTable({ entries }: { entries: any[] }) {
    if (!entries.length) return <p style={{ textAlign: 'center' }}>Sem lançamentos neste filtro.</p>;
    const rows = entries.map((e) => ({
        ...e,
        sourceLabel: SOURCE_LABEL[e.source] || e.source,
        availableAt: e.availableAt ?? e.available_at ?? null,
        createdAt: e.createdAt ?? e.created_at,
    }));
    return (
        <TableWithSearchAndSortEngine items={rows} header={[
            { label: 'Valor', id: 'value', allowSort: true, showValue: (e) => <span style={{ color: e.value < 0 ? '#c62828' : '#2e7d32' }}>{formatLocalCurrency(centsToReais(e.value))}</span> },
            { label: 'Origem', id: 'sourceLabel' },
            { label: 'Descrição', id: 'description' },
            { label: 'Situação', id: 'status', showValue: (e) => e.value < 0 ? '-' : (e.availableAt && new Date(e.availableAt).getTime() > Date.now())
                ? <Chip size="small" color="warning" label={`Libera ${formatDateWithMin(e.availableAt).slice(0, 10)}`} />
                : <Chip size="small" color="success" label="Disponível" /> },
            { label: 'Data', id: 'createdAt', allowSort: true, showValue: (e) => formatDateWithMin(e.createdAt) },
        ]} />
    );
}

/** Caixa do atendente: saldo disponível/pendente e extrato com filtros de período e origem. */
export function AgentCash() {
    const [data, setData] = useState<any>(null);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [source, setSource] = useState('');
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await hsRequest('GET', `${HS_AGENT}/cash`, undefined, {
                from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
                to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined, source,
            }));
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setLoading(false);
    }, [from, to, source]);
    useEffect(() => { load(); }, [load]);
    return (
        <Box>
            {data ? <CashSummaryCards summary={data.summary} /> : null}
            {data ? <p style={{ textAlign: 'center', fontSize: 13, opacity: 0.7 }}>
                Comissões ficam pendentes por {data.graceDays} dias após o pagamento da proposta e depois viram saldo disponível.
            </p> : null}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', my: 2 }}>
                <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
                <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
                <TextField size="small" select label="Origem" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 190 }}>
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="human_sales">Atendimento (comissão)</MenuItem>
                    <MenuItem value="manual_payment">Pagamentos</MenuItem>
                    <MenuItem value="manual_adjustment">Ajustes</MenuItem>
                </TextField>
                <LoadingButton variant="contained" loading={loading} onClick={load}>Atualizar</LoadingButton>
            </Box>
            {loading && !data ? <CircularLoading /> : <EntriesTable entries={data?.entries || []} />}
        </Box>
    );
}
